import { z } from 'zod';

/*
 * Client-side JWT inspection. We never verify the signature here — that's the
 * backend's job on every request. The payload is read-only context for routing
 * (which dashboard to land on, what to gate). A tampered token gets rejected
 * server-side with a 401 and the response hook clears auth.
 */

const claimsSchema = z.object({
  sub: z.string().optional(),
  client_id: z.string().optional(),
  role: z.string().optional(),
  allowed_reports: z.array(z.string()).optional(),
  iat: z.number().optional(),
  exp: z.number().optional(),
});

export type TokenClaims = z.infer<typeof claimsSchema>;

function base64UrlDecode(input: string): string {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return atob(padded);
}

export function decodeJwt(token: string): TokenClaims | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const json = base64UrlDecode(parts[1]);
    const parsed = claimsSchema.safeParse(JSON.parse(json));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function readAllowedReports(token: string): string[] {
  return decodeJwt(token)?.allowed_reports ?? [];
}
